import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, FileText, Maximize2, BookOpen } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MediaGallerySection — public renderer for the media_gallery section type.
 * Supports: image + PDF display, click-to-lightbox (optional), download button (optional),
 * multiple layouts (grid/row/single), keyboard navigation in lightbox.
 */
export default function MediaGallerySection({ content = {}, sectionId }) {
  const {
    title,
    subtitle,
    layout = 'grid',
    columns = 3,
    enable_lightbox = true,
    enable_download = false,
    items = [],
  } = content;

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const validItems = (items || []).filter(i => i && (i.url || i.src));

  const openLightbox = (idx) => {
    if (!enable_lightbox) return;
    if (validItems[idx]?.type === 'pdf') return; // open PDF in new tab instead
    setLightboxIndex(idx);
  };

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const next = useCallback(() => setLightboxIndex(i => (i === null ? null : (i + 1) % validItems.length)), [validItems.length]);
  const prev = useCallback(() => setLightboxIndex(i => (i === null ? null : (i - 1 + validItems.length) % validItems.length)), [validItems.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [lightboxIndex, closeLightbox, next, prev]);

  if (!validItems.length && !title) return null;

  const gridCols = layout === 'grid'
    ? { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4' }[Math.max(2, Math.min(4, columns || 3))]
    : '';
  const containerClass = layout === 'grid'
    ? `grid grid-cols-1 ${gridCols} gap-4`
    : layout === 'row'
      ? 'flex flex-wrap gap-4'
      : 'space-y-4';

  return (
    <section id={sectionId} className="py-14 bg-white" data-testid={`media-gallery-${sectionId || 'section'}`}>
      <div className="max-w-6xl mx-auto px-4">
        {title && (
          <div className="mb-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-gray-600 mt-3 max-w-2xl mx-auto">{subtitle}</p>}
          </div>
        )}
        <div className={containerClass}>
          {validItems.map((item, idx) => (
            <MediaCard
              key={item.id || `${item.url}-${idx}`}
              item={item}
              layout={layout}
              enableLightbox={enable_lightbox}
              enableDownload={enable_download}
              onClick={() => openLightbox(idx)}
            />
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          item={validItems[lightboxIndex]}
          onClose={closeLightbox}
          onNext={validItems.length > 1 ? next : null}
          onPrev={validItems.length > 1 ? prev : null}
          enableDownload={enable_download}
          index={lightboxIndex}
          total={validItems.length}
        />
      )}
    </section>
  );
}

function MediaCard({ item, layout, enableLightbox, enableDownload, onClick }) {
  const url = item.url || item.src;
  const absUrl = url?.startsWith('http') ? url : `${API}${url}`;
  const isPdf = item.type === 'pdf' || url?.toLowerCase().endsWith('.pdf');
  const caption = item.caption || item.filename || '';

  const containerBase = layout === 'row' ? 'w-64 flex-shrink-0' : 'w-full';

  if (isPdf) {
    return (
      <div className={`${containerBase} border rounded-xl bg-white hover:shadow-xl transition-all duration-300 overflow-hidden group`}>
        <a href={absUrl} target="_blank" rel="noopener noreferrer" className="block relative h-48 bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <Document file={absUrl} loading="" error="">
              <Page
                pageNumber={1}
                width={220}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading=""
                error=""
                className="shadow-md rounded-sm pointer-events-none"
              />
            </Document>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="px-2 py-0.5 bg-red-600/90 text-white text-[10px] font-bold uppercase tracking-wider rounded-md backdrop-blur-sm">PDF</span>
            <Maximize2 className="w-4 h-4 text-white drop-shadow" />
          </div>
        </a>
        <div className="p-3 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-800 truncate">{caption || 'Document'}</div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a href={absUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Open"><Maximize2 className="w-3.5 h-3.5" /></a>
            {enableDownload && (
              <a href={absUrl} download={caption || 'download.pdf'} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Download"><Download className="w-3.5 h-3.5" /></a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${containerBase} group border rounded-xl overflow-hidden bg-white hover:shadow-lg transition-shadow`}>
      <button
        onClick={onClick}
        className={`relative w-full block ${enableLightbox ? 'cursor-zoom-in' : 'cursor-default'}`}
        aria-label={enableLightbox ? 'Zoom image' : undefined}
        disabled={!enableLightbox}
      >
        <img src={absUrl} alt={caption || 'Media item'} className="w-full aspect-[4/3] object-cover" loading="lazy" />
        {enableLightbox && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-colors">
            <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
          </div>
        )}
      </button>
      {(caption || enableDownload) && (
        <div className="p-3 flex items-center justify-between gap-2">
          <div className="text-sm text-gray-700 truncate flex-1">{caption}</div>
          {enableDownload && (
            <a href={absUrl} download={caption || 'download'} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-500 hover:text-emerald-700 hover:bg-emerald-50 rounded flex-shrink-0" title="Download"><Download className="w-3.5 h-3.5" /></a>
          )}
        </div>
      )}
    </div>
  );
}

function Lightbox({ item, onClose, onNext, onPrev, enableDownload, index, total }) {
  const url = item.url || item.src;
  const absUrl = url?.startsWith('http') ? url : `${API}${url}`;
  const [zoom, setZoom] = useState(1);
  const caption = item.caption || item.filename || '';

  useEffect(() => { setZoom(1); }, [item]);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 flex flex-col"
      onClick={onClose}
      data-testid="media-lightbox"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between p-4 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm opacity-80">{total > 1 ? `${index + 1} of ${total}` : ''} {caption && <span className="ml-2 font-medium">{caption}</span>}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} className="p-2 rounded hover:bg-white/10" data-testid="lightbox-zoom-in" aria-label="Zoom in"><ZoomIn className="w-5 h-5" /></button>
          <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} className="p-2 rounded hover:bg-white/10" data-testid="lightbox-zoom-out" aria-label="Zoom out"><ZoomOut className="w-5 h-5" /></button>
          {enableDownload && (
            <a href={absUrl} download={caption || 'download'} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="p-2 rounded hover:bg-white/10" data-testid="lightbox-download" aria-label="Download"><Download className="w-5 h-5" /></a>
          )}
          <button onClick={onClose} className="p-2 rounded hover:bg-white/10" data-testid="lightbox-close" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Center image */}
      <div className="flex-1 flex items-center justify-center overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
        <img
          src={absUrl}
          alt={caption || 'Zoomed media'}
          style={{ transform: `scale(${zoom})`, transition: 'transform 0.2s' }}
          className="max-w-full max-h-full object-contain select-none"
        />
      </div>

      {/* Nav arrows */}
      {onPrev && (
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white bg-black/40 hover:bg-black/60 rounded-full" data-testid="lightbox-prev" aria-label="Previous"><ChevronLeft className="w-6 h-6" /></button>
      )}
      {onNext && (
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white bg-black/40 hover:bg-black/60 rounded-full" data-testid="lightbox-next" aria-label="Next"><ChevronRight className="w-6 h-6" /></button>
      )}
    </div>
  );
}
